import { useRef, useState, type ReactElement } from 'react';
import {
  addEasternDays,
  easternDateKey,
  easternMinutesFromMidnight,
  easternWallClockToUtc,
  easternZoneAbbrev,
  formatEasternClock,
  formatEasternDayHeader,
} from '../../utils/eastern-week';

const START_HOUR = 8;
const END_HOUR = 20;
const HOUR_PX = 36;
const LANE_HEIGHT = (END_HOUR - START_HOUR) * HOUR_PX;
const SNAP_MINUTES = 15;
const MIN_MINUTES = 30;
const MIN_DRAG_PX = 8;
const MAX_SUGGESTIONS = 6;
const HORIZON_DAYS = 14;
const VISIBLE_DAYS = 7;

export interface SuggestedRange {
  startAt: string;
  endAt: string;
}

interface SchedulingConflictTimeGridProps {
  suggestions: SuggestedRange[];
  disabled?: boolean;
  heading?: string;
  onChange: (next: SuggestedRange[]) => void;
}

interface DragState {
  originY: number;
  currentY: number;
}

function yToMinute(y: number): number {
  const span = (END_HOUR - START_HOUR) * 60;
  const raw = START_HOUR * 60 + (Math.max(0, Math.min(LANE_HEIGHT, y)) / LANE_HEIGHT) * span;
  const snapped = Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES;
  return Math.max(START_HOUR * 60, Math.min(END_HOUR * 60, snapped));
}

function minuteToIso(dateKey: string, minute: number): string {
  return easternWallClockToUtc(dateKey, Math.floor(minute / 60), minute % 60).toISOString();
}

function rangeLabel(range: SuggestedRange): string {
  const day = formatEasternDayHeader(easternDateKey(new Date(range.startAt)));
  return `${day.weekday} ${day.date}, ${formatEasternClock(range.startAt)}–${formatEasternClock(range.endAt, true)}`;
}

export default function SchedulingConflictTimeGrid({
  suggestions,
  disabled = false,
  heading = 'Times that could work',
  onChange,
}: SchedulingConflictTimeGridProps): ReactElement {
  const laneRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dayOffset, setDayOffset] = useState(0);
  const todayKey = easternDateKey(new Date());
  // Same-day tours are not scheduled, so the first selectable day is tomorrow.
  const firstKey = addEasternDays(todayKey, 1);
  const dayKeys = Array.from({ length: HORIZON_DAYS - 1 }, (_, index) => addEasternDays(firstKey, index));
  const visibleKeys = dayKeys.slice(dayOffset, dayOffset + VISIBLE_DAYS);
  const [selectedKey, setSelectedKey] = useState(firstKey);
  const zone = easternZoneAbbrev();

  const daySuggestions = suggestions.filter(
    (range) => easternDateKey(new Date(range.startAt)) === selectedKey,
  );

  const commitRange = (startMinute: number, endMinute: number): void => {
    let start = Math.min(startMinute, endMinute);
    let end = Math.max(startMinute, endMinute);
    if (end - start < MIN_MINUTES) end = start + MIN_MINUTES;
    if (end > END_HOUR * 60) {
      end = END_HOUR * 60;
      start = end - MIN_MINUTES;
    }
    if (start < START_HOUR * 60 || end <= start) {
      setError('Suggested times have to fall between 8:00 AM and 8:00 PM Eastern.');
      return;
    }
    if (suggestions.length >= MAX_SUGGESTIONS) {
      setError(`Suggest at most ${MAX_SUGGESTIONS} times.`);
      return;
    }
    const startAt = minuteToIso(selectedKey, start);
    const endAt = minuteToIso(selectedKey, end);
    if (new Date(startAt).getTime() <= Date.now()) {
      setError('Suggested times have to be in the future.');
      return;
    }
    const startMs = new Date(startAt).getTime();
    const endMs = new Date(endAt).getTime();
    const overlaps = suggestions.some((range) => {
      const left = new Date(range.startAt).getTime();
      const right = new Date(range.endAt).getTime();
      return startMs < right && endMs > left;
    });
    if (overlaps) {
      setError('Suggested times cannot overlap.');
      return;
    }
    setError(null);
    onChange([...suggestions, { startAt, endAt }].sort((left, right) => left.startAt.localeCompare(right.startAt)));
  };

  const laneY = (event: React.PointerEvent<HTMLDivElement>): number => {
    const rect = laneRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    return event.clientY - rect.top;
  };

  const ghost = (() => {
    if (!drag) return null;
    if (Math.abs(drag.currentY - drag.originY) < MIN_DRAG_PX) return null;
    const start = yToMinute(Math.min(drag.originY, drag.currentY));
    let end = yToMinute(Math.max(drag.originY, drag.currentY));
    if (end - start < MIN_MINUTES) end = Math.min(END_HOUR * 60, start + MIN_MINUTES);
    const top = ((start - START_HOUR * 60) / ((END_HOUR - START_HOUR) * 60)) * LANE_HEIGHT;
    const height = ((end - start) / ((END_HOUR - START_HOUR) * 60)) * LANE_HEIGHT;
    return { top, height };
  })();

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-900 dark:text-white">{heading}</p>
        <p className="text-xs tabular-nums text-gray-500 dark:text-gray-400">{zone}</p>
      </div>
      <div className="mb-2 flex items-center gap-1">
        <button
          type="button"
          disabled={disabled || dayOffset === 0}
          onClick={() => {
            const nextOffset = Math.max(0, dayOffset - VISIBLE_DAYS);
            setDayOffset(nextOffset);
            const visible = dayKeys.slice(nextOffset, nextOffset + VISIBLE_DAYS);
            if (!visible.includes(selectedKey)) setSelectedKey(visible[0] ?? selectedKey);
          }}
          className="rounded-md px-2 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-40 dark:text-gray-300 dark:hover:bg-white/10"
          aria-label="Earlier days"
        >
          Prev
        </button>
        <div className="grid min-w-0 flex-1 grid-cols-7 gap-1">
          {visibleKeys.map((key) => {
            const header = formatEasternDayHeader(key);
            const selected = key === selectedKey;
            const hasRange = suggestions.some((range) => easternDateKey(new Date(range.startAt)) === key);
            return (
              <button
                key={key}
                type="button"
                disabled={disabled}
                onClick={() => setSelectedKey(key)}
                className={`rounded-md px-1 py-1 text-center ${
                  selected
                    ? 'bg-red-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10'
                }`}
              >
                <span className="block text-[10px] font-semibold uppercase">{header.weekday}</span>
                <span className="block text-xs tabular-nums">{Number(key.slice(8))}</span>
                {hasRange ? <span className={`mx-auto mt-0.5 block h-1 w-1 rounded-full ${selected ? 'bg-white' : 'bg-red-600'}`} /> : null}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          disabled={disabled || dayOffset + VISIBLE_DAYS >= HORIZON_DAYS}
          onClick={() => {
            const nextOffset = Math.min(HORIZON_DAYS - VISIBLE_DAYS, dayOffset + VISIBLE_DAYS);
            setDayOffset(nextOffset);
            const visible = dayKeys.slice(nextOffset, nextOffset + VISIBLE_DAYS);
            if (!visible.includes(selectedKey)) setSelectedKey(visible[visible.length - 1] ?? selectedKey);
          }}
          className="rounded-md px-2 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-40 dark:text-gray-300 dark:hover:bg-white/10"
          aria-label="Later days"
        >
          Next
        </button>
      </div>
      <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
        Drag on the day to suggest a time between 8:00 AM and 8:00 PM. Click a block to remove it.
      </p>
      <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200 dark:border-white/10">
        <div className="flex">
          <div className="relative w-14 shrink-0 border-r border-gray-100 dark:border-white/10" style={{ height: LANE_HEIGHT }}>
            {Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, index) => {
              const hour = START_HOUR + index;
              const labelHour = hour % 12 || 12;
              const period = hour >= 12 && hour < 24 ? 'PM' : 'AM';
              const edge = hour === START_HOUR ? 'top-0.5' : hour === END_HOUR ? 'bottom-0.5' : '-translate-y-1/2';
              return (
                <span
                  key={hour}
                  className={`absolute right-1 text-[10px] tabular-nums leading-none text-gray-400 dark:text-gray-500 ${edge}`}
                  style={hour === START_HOUR || hour === END_HOUR ? undefined : { top: index * HOUR_PX }}
                >
                  {hour === END_HOUR ? '8 PM' : `${labelHour} ${period}`}
                </span>
              );
            })}
          </div>
          <div
            ref={laneRef}
            className="relative min-w-0 flex-1 touch-none bg-white dark:bg-gray-900"
            style={{ height: LANE_HEIGHT }}
            onPointerDown={(event) => {
              if (disabled || event.button !== 0) return;
              if ((event.target as HTMLElement).closest('[data-suggestion]')) return;
              event.currentTarget.setPointerCapture(event.pointerId);
              const next = { originY: laneY(event), currentY: laneY(event) };
              dragRef.current = next;
              setDrag(next);
            }}
            onPointerMove={(event) => {
              const current = dragRef.current;
              if (!current) return;
              const next = { originY: current.originY, currentY: laneY(event) };
              dragRef.current = next;
              setDrag(next);
            }}
            onPointerUp={(event) => {
              const current = dragRef.current;
              dragRef.current = null;
              setDrag(null);
              if (!current) return;
              const y = laneY(event);
              if (Math.abs(y - current.originY) < MIN_DRAG_PX) return;
              commitRange(yToMinute(current.originY), yToMinute(y));
            }}
            onPointerCancel={() => {
              dragRef.current = null;
              setDrag(null);
            }}
          >
            {Array.from({ length: END_HOUR - START_HOUR }, (_, index) => (
              <div
                key={index}
                className="absolute inset-x-0 border-t border-gray-100 dark:border-white/10"
                style={{ top: index * HOUR_PX }}
              />
            ))}
            {daySuggestions.map((range) => {
              const start = easternMinutesFromMidnight(new Date(range.startAt));
              const end = easternMinutesFromMidnight(new Date(range.endAt));
              const top = ((start - START_HOUR * 60) / ((END_HOUR - START_HOUR) * 60)) * LANE_HEIGHT;
              const height = Math.max(18, ((end - start) / ((END_HOUR - START_HOUR) * 60)) * LANE_HEIGHT);
              return (
                <button
                  key={`${range.startAt}-${range.endAt}`}
                  type="button"
                  data-suggestion="true"
                  disabled={disabled}
                  onClick={() => {
                    setError(null);
                    onChange(suggestions.filter((item) => item.startAt !== range.startAt || item.endAt !== range.endAt));
                  }}
                  className="absolute inset-x-1 overflow-hidden rounded-md bg-red-600 px-2 text-left text-[11px] font-semibold text-white"
                  style={{ top, height }}
                  aria-label={`Remove ${rangeLabel(range)}`}
                >
                  <span className="block truncate whitespace-nowrap">
                    {formatEasternClock(range.startAt)}–{formatEasternClock(range.endAt)}
                  </span>
                </button>
              );
            })}
            {ghost ? (
              <div
                className="pointer-events-none absolute inset-x-1 rounded-md border border-dashed border-red-600 bg-red-500/20"
                style={{ top: ghost.top, height: Math.max(12, ghost.height) }}
              />
            ) : null}
          </div>
        </div>
      </div>
      {error ? <p className="mt-2 text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
      {suggestions.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {suggestions.map((range) => (
            <li key={`${range.startAt}-${range.endAt}`} className="truncate text-xs tabular-nums text-gray-600 dark:text-gray-300">
              {rangeLabel(range)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
